import type {
  AbstractPartDefinition,
  DragConstraint,
  SerializableVector3,
} from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import * as THREE from "three";
import { Alignment } from "../enum/Alignment";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import type { AddableGridInfos } from "../interfaces/AddableGridInfos";
import { RectPropertiesBySide } from "../structure/RectProperties";
import {
  normalizeAddableAlignment,
  positionMarginOffsetOnAxis,
  readAddableCutSize,
  readResolveAxisMids,
} from "./addableDragGrid";

function getCut(
  orientation: number,
  orientationOnRect: OrientationOnRect,
  cutX?: number,
  cutY?: number,
): number | undefined {
  const a = orientation % 180 !== 0;
  const b = orientationOnRect === OrientationOnRect.HORIZONTAL;

  if (a !== b) {
    return cutX ? cutX / 2 : undefined;
  }
  return cutY ? cutY / 2 : undefined;
}

/**
 * Same formula as AddableBuildStep.updateSettingsFromDrag (without clamping to ≥ 0).
 */
function marginFromOffset(
  offset: number,
  size: number,
  count: number,
  n: number,
  padding: number,
  alignmentCtx: Alignment,
  mirrored: boolean,
): number {
  const pos = offset + size / 2;
  if (count > 1 || alignmentCtx !== Alignment.CENTER) {
    const denom = 1 - 2 * n;
    if (Math.abs(denom) < 0.001) {
      return Number.NaN;
    }
    return (pos - size * n - padding * denom) / denom;
  }
  return mirrored ? -offset : offset;
}

export interface MarginAxisRange {
  min: number;
  max: number;
}

export interface AddableMarginDragRanges {
  horizontal: MarginAxisRange;
  vertical: MarginAxisRange;
}

type BoundsBox = {
  min: SerializableVector3;
  max: SerializableVector3;
};

function clampAxis(value: number, min: number, max: number): number {
  return Math.max(Math.min(min, max), Math.min(Math.max(min, max), value));
}

function projectOntoAreaBounds(
  point: THREE.Vector3,
  planeNormal: THREE.Vector3,
  bounds: BoundsBox,
): THREE.Vector3 {
  const min = new THREE.Vector3(...bounds.min);
  const max = new THREE.Vector3(...bounds.max);
  const projected = point.clone();
  if (Math.abs(planeNormal.x) < 0.9) {
    projected.x = clampAxis(projected.x, min.x, max.x);
  }
  if (Math.abs(planeNormal.y) < 0.9) {
    projected.y = clampAxis(projected.y, min.y, max.y);
  }
  if (Math.abs(planeNormal.z) < 0.9) {
    projected.z = clampAxis(projected.z, min.z, max.z);
  }
  return projected;
}

/** Panel whose in-plane clamp is closest to the drag position (matches viewer). */
export function pickActiveAreaBounds(
  constraint: DragConstraint,
  position: THREE.Vector3,
): BoundsBox {
  if (!constraint.areas?.length) {
    return constraint.bounds;
  }
  const planeNormal = new THREE.Vector3(
    ...(constraint.planeNormal as SerializableVector3),
  );
  let bestIdx = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  constraint.areas.forEach((area, i) => {
    const projected = projectOntoAreaBounds(position, planeNormal, area.bounds);
    const dist = position.distanceTo(projected);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  });
  return constraint.areas[bestIdx]?.bounds ?? constraint.bounds;
}

function cornersFromBoundsBox(bounds: BoundsBox): THREE.Vector3[] {
  const corners: THREE.Vector3[] = [];
  for (const x of [bounds.min[0], bounds.max[0]]) {
    for (const y of [bounds.min[1], bounds.max[1]]) {
      for (const z of [bounds.min[2], bounds.max[2]]) {
        corners.push(new THREE.Vector3(x, y, z));
      }
    }
  }
  return corners;
}

function unionMarginRanges(
  a: AddableMarginDragRanges,
  b: AddableMarginDragRanges,
): AddableMarginDragRanges {
  return {
    horizontal: {
      min: Math.min(a.horizontal.min, b.horizontal.min),
      max: Math.max(a.horizontal.max, b.horizontal.max),
    },
    vertical: {
      min: Math.min(a.vertical.min, b.vertical.min),
      max: Math.max(a.vertical.max, b.vertical.max),
    },
  };
}

function computeMarginRangesFromBoundsBox(
  bounds: BoundsBox,
  ctx: Record<string, unknown>,
  option: Pick<
    AddableGridInfos,
    "orientation" | "partSelection" | "width" | "height"
  >,
): AddableMarginDragRanges | null {
  const side = ctx.side as Sides;
  const rectProps = RectPropertiesBySide[side];
  if (!rectProps) {
    return null;
  }

  const { horizontalDir, verticalDir, horizontal, vertical } = rectProps;
  const resolveMids = readResolveAxisMids(ctx);

  const logicalRectBoundsMin = new THREE.Vector3(
    ...(ctx.logicalRectBoundsMin as [number, number, number]),
  );
  const logicalRectBoundsMax = new THREE.Vector3(
    ...(ctx.logicalRectBoundsMax as [number, number, number]),
  );
  const logicalRectCenter = new THREE.Vector3(
    ...(ctx.logicalRectCenter as [number, number, number]),
  );

  const horizontalCount = (ctx.horizontalCount as number) ?? 1;
  const verticalCount = (ctx.verticalCount as number) ?? 1;
  const horizontalN = (ctx.horizontalN as number) ?? 0.5;
  const verticalN = (ctx.verticalN as number) ?? 0.5;
  const horizontalAlignmentCtx = normalizeAddableAlignment(
    ctx.horizontalAlignment,
  );
  const verticalAlignmentCtx = normalizeAddableAlignment(ctx.verticalAlignment);
  const mirrored = (ctx.mirrored as boolean) ?? false;

  const rectSize = logicalRectBoundsMax.clone().sub(logicalRectBoundsMin);
  const horizontalSize = Math.abs(rectSize.dot(horizontalDir));
  const verticalSize = Math.abs(rectSize.dot(verticalDir));

  const { cutX, cutY } = readAddableCutSize(option);
  const paddingX =
    getCut(option.orientation, OrientationOnRect.HORIZONTAL, cutX, cutY) || 0;
  const paddingY =
    getCut(option.orientation, OrientationOnRect.VERTICAL, cutX, cutY) || 0;

  const horizontalMargins: number[] = [];
  const verticalMargins: number[] = [];

  for (const p of cornersFromBoundsBox(bounds)) {
    let horizontalOffset: number;
    let verticalOffset: number;
    if (resolveMids) {
      horizontalOffset = positionMarginOffsetOnAxis(
        p,
        resolveMids.horizontal,
        horizontalDir,
        horizontal,
      );
      verticalOffset = positionMarginOffsetOnAxis(
        p,
        resolveMids.vertical,
        verticalDir,
        vertical,
      );
    } else {
      const offsetFromCenter = p.clone().sub(logicalRectCenter);
      horizontalOffset = offsetFromCenter.dot(horizontalDir);
      verticalOffset = offsetFromCenter.dot(verticalDir);
    }
    const orient = option.orientation ?? 0;
    if (horizontal === "z" && side === Sides.LEFT && orient % 180 !== 0) {
      horizontalOffset = -horizontalOffset;
    }

    const mH = marginFromOffset(
      horizontalOffset,
      horizontalSize,
      horizontalCount,
      horizontalN,
      paddingX,
      horizontalAlignmentCtx,
      mirrored,
    );
    const mV = marginFromOffset(
      verticalOffset,
      verticalSize,
      verticalCount,
      verticalN,
      paddingY,
      verticalAlignmentCtx,
      mirrored,
    );

    if (Number.isFinite(mH)) horizontalMargins.push(mH);
    if (Number.isFinite(mV)) verticalMargins.push(mV);
  }

  if (horizontalMargins.length === 0 || verticalMargins.length === 0) {
    return null;
  }

  return {
    horizontal: {
      min: Math.min(...horizontalMargins),
      max: Math.max(...horizontalMargins),
    },
    vertical: {
      min: Math.min(...verticalMargins),
      max: Math.max(...verticalMargins),
    },
  };
}

/**
 * Margin range for the panel the pointer is currently over. Uses that panel's
 * bounds only — the union bounding box can include phantom corners (gaps between
 * split panels) whose margins lie outside any real drag position.
 */
export function computeAddableMarginRangesForDragPosition(
  part: AbstractPartDefinition<string>,
  option: Pick<
    AddableGridInfos,
    "orientation" | "partSelection" | "width" | "height"
  >,
  position: THREE.Vector3,
): AddableMarginDragRanges | null {
  const constraint = part.dragConstraint;
  const ctx = part.settingsContext;
  if (constraint?.type !== "surface" || !ctx) {
    return null;
  }
  const bounds = pickActiveAreaBounds(constraint, position);
  return computeMarginRangesFromBoundsBox(bounds, ctx, option);
}

/**
 * Derive min/max horizontal and vertical margins from the same padded 3D bounds
 * used for Ctrl+drag on addables (surface drag constraint corners → margin pairs).
 * With multiple `areas`, unions the per-panel ranges instead of the union box
 * corners (which can overstate the achievable margin).
 */
export function computeAddableMarginRangesFromDragPart(
  part: AbstractPartDefinition<string>,
  option: Pick<
    AddableGridInfos,
    "orientation" | "partSelection" | "width" | "height"
  >,
): AddableMarginDragRanges | null {
  const constraint = part.dragConstraint;
  const ctx = part.settingsContext;
  if (constraint?.type !== "surface" || !ctx) {
    return null;
  }

  if (constraint.areas?.length) {
    let combined: AddableMarginDragRanges | null = null;
    for (const area of constraint.areas) {
      const range = computeMarginRangesFromBoundsBox(area.bounds, ctx, option);
      if (!range) {
        continue;
      }
      combined = combined ? unionMarginRanges(combined, range) : range;
    }
    return combined;
  }

  return computeMarginRangesFromBoundsBox(constraint.bounds, ctx, option);
}

/**
 * Intersect two margin ranges (per axis). Used to combine the per-part ranges of
 * a multi-cell addable grid into the single range that keeps every cell of the
 * shared option inside its drag area.
 */
export function intersectMarginRanges(
  a: AddableMarginDragRanges,
  b: AddableMarginDragRanges,
): AddableMarginDragRanges {
  return {
    horizontal: {
      min: Math.max(a.horizontal.min, b.horizontal.min),
      max: Math.min(a.horizontal.max, b.horizontal.max),
    },
    vertical: {
      min: Math.max(a.vertical.min, b.vertical.min),
      max: Math.min(a.vertical.max, b.vertical.max),
    },
  };
}

/**
 * Clamp a stored margin into the achievable range, keeping it an integer that
 * stays *inside* the (float) range — the upper bound is floored, the lower bound
 * ceiled.
 *
 * When the range holds less than 1 mm of play no integer fits inside it; the
 * result is then the integer nearest to the value the user asked for, rather
 * than the range midpoint, so a typed margin is never replaced by a number that
 * relates to neither the input nor a reachable bound.
 *
 * A range left disjoint by {@link intersectMarginRanges} (`min > max`, i.e. no
 * single margin keeps every part of a grid inside its area) is treated as the
 * span between both bounds: there is no correct answer, so the stored value is
 * preferred over moving the part to an arbitrary end.
 */
export function clampMarginToRange(
  value: number,
  range: MarginAxisRange,
): number {
  const rangeMin = Math.min(range.min, range.max);
  const rangeMax = Math.max(range.min, range.max);
  const lo = Math.ceil(rangeMin);
  const hi = Math.floor(rangeMax);
  if (hi < lo) {
    return Math.round(Math.min(Math.max(value, rangeMin), rangeMax));
  }
  if (value < lo) return lo;
  if (value > hi) return hi;
  return value;
}

/**
 * The whole-mm bounds {@link clampMarginToRange} keeps a margin in, for an input
 * that should offer exactly what the build accepts: a value the input allows but
 * the build clamps would read as a clamp the user caused by typing the shown
 * maximum. With less than 1 mm of play no integer fits; the bounds then span the
 * integers around the range, which is where the clamp rounds to.
 */
export function integerMarginBounds(range: MarginAxisRange): MarginAxisRange {
  const rangeMin = Math.min(range.min, range.max);
  const rangeMax = Math.max(range.min, range.max);
  const lo = Math.ceil(rangeMin);
  const hi = Math.floor(rangeMax);
  return hi < lo
    ? { min: Math.round(rangeMin), max: Math.round(rangeMax) }
    : { min: lo, max: hi };
}
